import { AppFunctionComponent } from "../../../types"
import { BLOCKS, Node } from "@contentful/rich-text-types"
import { graphql, navigate, useStaticQuery } from "gatsby"
import React, { useState } from "react"
import styled from "styled-components"
import {
  isButton,
  mapEmbeddedEntryToButton,
} from "../../../contentful/options/rich-text"
import { useLocalizationContext } from "../../../context/localization.context"
import { generateSources } from "../../../helpers/images"
import useTranslationHook from "../../../hooks/use-translation.hook"
import { Path } from "../../../navigation/constants/routes"
import {
  createSarCheckerPhoneSlug,
  PhoneData,
} from "../../../pages-creators/sar-checker-phone.creator"
import RichText from "../../../resolvers/rich-text/rich-text.resolver"
import { GatsbyImage, HeroSectionQueryResultTranslation } from "../../../types"
import { rawLinkStyles } from "../../../ui/components/link/link.component"
import { PrimaryHeading } from "../../common/heading.component"
import { Image } from "../../common/image.component"
import InputSearch from "../../common/input-search/input-search"
import { ResultItem } from "../../common/input-search/results.component"
import Hero, {
  HeroDesktopLayout,
  HeroHeading,
  HeroMobileLayout,
  HeroText,
} from "../../common/sections/hero-section-refactored.component"
import TellUsModal from "./tell-us-modal.component"

const HeroContainer = styled(Hero)`
  --min-hero-height: 475px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    --min-hero-height: 660px;
  }

  ${HeroHeading} {
    margin-bottom: 25px;
    padding: 0;
  }

  ${PrimaryHeading} {
    margin: 0 auto 25px;
  }

  ${HeroText} {
    padding: 0;
  }
`

const HeroForm = styled.div`
  z-index: 3;
  margin-bottom: 20px;
  max-width: 420px;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    margin-bottom: 30px;
  }
`

const HeroBackgroundImage = styled(Image)`
  position: absolute;
  bottom: 0;
  left: 0;

  width: 100%;
`

const SarCheckerHeroButton = styled.button`
  ${rawLinkStyles};
  border: none;
  outline: 0;
  background-color: transparent;
  font-weight: 300;
  opacity: 1;
  cursor: pointer;
`

const ScrollHandler = styled.span`
  position: absolute;
  top: calc(var(--header-height) * -1);
`

interface ButtonLinkProps {
  text: string
  onClick: () => void
}

const ActivateModalButton: AppFunctionComponent<ButtonLinkProps> = ({
  text,
  onClick,
}) => {
  return <SarCheckerHeroButton onClick={onClick}>{text}</SarCheckerHeroButton>
}

const mapToResultItems = (
  phones: ContentfulSarChceckerPhoneConnection,
  placeholderImage: GatsbyImage
): ResultItem[] => {
  return phones.edges.map(({ node: { name, model, image } }) => ({
    id: createSarCheckerPhoneSlug({ name, model }),
    title: name,
    subtitle: model,
    image: image ? image.gatsbyImageData : placeholderImage.gatsbyImageData,
  }))
}

interface ContentfulSarChceckerPhoneConnection {
  edges: {
    node: PhoneData
  }[]
}

interface SarCheckerHeroSectionQueryResult
  extends HeroSectionQueryResultTranslation {
  phones: ContentfulSarChceckerPhoneConnection
  placeholder: {
    image: GatsbyImage
  }
}

const SarCheckerHero: AppFunctionComponent = () => {
  const {
    content: { nodes },
    phones,
    placeholder,
  }: SarCheckerHeroSectionQueryResult = useStaticQuery(query)
  const { locale } = useLocalizationContext()
  const { translate } = useTranslationHook()
  const translatedContent =
    nodes.find((node) => node.node_locale === locale) || nodes[0]
  const { mobileImage, tabletImage, desktopImage, primaryTitle, text } =
    translatedContent

  const resultItems = mapToResultItems(phones, placeholder.image)

  const [active, setActive] = useState(false)

  const activateModal = () => setActive(true)
  const deactivateModal = () => setActive(false)

  const redirectToPhone = (id: string) => navigate(`${Path.SarChecker}${id}`)

  return (
    <HeroContainer
      mobileLayout={HeroMobileLayout.CenteredSingleColumn}
      desktopLayout={HeroDesktopLayout.CenteredSingleColumn}
    >
      <ScrollHandler id={"sar-checker"} />
      <HeroHeading>
        <PrimaryHeading renderAs={"h1"}>{primaryTitle}</PrimaryHeading>
      </HeroHeading>
      <HeroForm>
        <InputSearch
          items={resultItems}
          placeholder={translate("sar-check-phone")}
          label={translate("sar-check-value-label")}
          newItemNotifyText={translate("sar-didnt-found")}
          onNewItemNotifyMouseDown={activateModal}
          onItemMouseDown={redirectToPhone}
        />
      </HeroForm>
      <HeroText>
        {text && (
          <RichText
            content={text}
            options={{
              [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
                if (isButton(node)) {
                  return (
                    <ActivateModalButton
                      {...mapEmbeddedEntryToButton(node)}
                      onClick={activateModal}
                    />
                  )
                }
                /* if needed then add other types to map */
                return null
              },
            }}
          />
        )}
      </HeroText>
      <HeroBackgroundImage
        image={generateSources([
          [desktopImage],
          [tabletImage, 1260],
          [mobileImage, 640],
        ])}
      />
      <TellUsModal active={active} onClose={deactivateModal} />
    </HeroContainer>
  )
}

export default SarCheckerHero

const query = graphql`
  query SarCheckerHeroSectionQuery {
    phones: allContentfulSarChceckerPhone {
      edges {
        node {
          name
          model
          image {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              quality: 100
              width: 40
            )
          }
        }
      }
    }
    content: allContentfulHeroSection(
      filter: { contentfulid: { eq: "sar-checker" } }
    ) {
      nodes {
        node_locale
        primaryTitle
        text {
          raw
          references {
            ...Button
          }
        }
        mobileImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 640
          )
        }
        tabletImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 1024
          )
        }
        desktopImage: image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            quality: 100
            width: 1260
          )
        }
      }
    }
    placeholder: file(relativePath: { eq: "phone-placeholder.png" }) {
      image: childImageSharp {
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: BLURRED
          quality: 100
          width: 40
        )
      }
    }
  }
`
